import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBack from '@mui/icons-material/ArrowBack'
import DragIndicator from '@mui/icons-material/DragIndicator'
import EditOutlined from '@mui/icons-material/EditOutlined'
import MoreVert from '@mui/icons-material/MoreVert'
import { aiAvailabilityKey, fetchAiAvailability, isAiDisabledHere, markAiDisabledHere } from '../api/aiAvailability'
import { SUGGESTABLE_BLOCKS } from '../api/aiSuggestions'
import { ApiError } from '../api/errors'
import { BANDS_QUERY_KEY, fetchBands, functionByJob, maiuscula } from '../api/bands'
import { fetchAllJobs, JOBS_QUERY_KEY } from '../api/jobs'
import {
  currentDescriptionKey,
  descriptionVersionsKey,
  fetchDescriptionVersions,
  fetchJobRecord,
  findDraft,
  jobRecordKey,
  textoParaTela,
  type DescriptionBlock,
  type ItemRequirement,
} from '../api/jobDescription'
import {
  activitiesLinkedCount,
  addItem,
  BLOCK_ORDER,
  canAddItem,
  descriptionKey,
  draftSource,
  editItem,
  fetchDescription,
  headerErrorField,
  itemsOfBlock,
  linkableDeliverables,
  moveItem,
  openDraft,
  publishDescription,
  removeItem,
  REQUIREMENT_BLOCKS,
  transversalActivities,
  updateHeader,
  type DescriptionVersion,
  type DraftItem,
  type HeaderChanges,
  type HeaderErrorField,
} from '../api/jobDescriptionDraft'
import { fetchMe, ME_QUERY_KEY } from '../api/session'
import { fetchAllUnits, UNITS_QUERY_KEY } from '../api/units'
import { AiSuggestionPanel } from '../components/AiSuggestionPanel'
import { VigaIcon } from '../components/VigaIcon'
import { VIGA } from '../components/viga'
import { ErrorState } from '../components/ErrorState'
import { LoadingState } from '../components/LoadingState'
import { ConfirmDialog, type Confirmacao } from '../components/ConfirmDialog'
import { t } from '../i18n'

/**
 * Tela 6 (sessao 43, F1) - EDITOR DO RASCUNHO da descricao do cargo, parte MANUAL.
 *
 * PARA QUEM (regra 47, corolario s32): quem tem `job_architecture.description.write`
 * (hr_manager e platform_admin, seed rbac da descricao); publicar exige `.publish`
 * (os mesmos dois papeis). Os demais veem so o aviso de permissao.
 *
 * Desenho (ADR-0027 nota de 2026-09-30, desenho 08, decisao S43 D2 F1):
 * - sem rascunho: abre um, copiando a PUBLICADA quando ela existe (I4), senao vazio;
 * - cabecalho por PRESENCA (so o que mudou); area/subordinacao vao como ID (I7);
 * - 8 blocos na ordem do PES.001; papel/experiencia com no maximo 1 item (I1);
 *   formacao/conhecimentos/treinamentos com exigencia; atividade ligada a uma ENTREGA (B3);
 * - publicar so com confirmacao; depois, a ficha e as versoes sao BUSCADAS DE NOVO, inclusive
 *   fechadas (o app usa staleTime de 30 s), e so entao a pessoa volta para a ficha (s45).
 * s59: PAINEL DE IA ao lado (Attio, S31-7) nos 4 blocos que a IA pode sugerir, para quem tem `ai_services.use`;
 *      a espera fica no painel e o editor continua usavel (G2 = A). Rota com id opaco (ADR-0024 D10.1).
 */

const PERM_WRITE = 'job_architecture.description.write'
const PERM_PUBLISH = 'job_architecture.description.publish'
const PERM_IA = 'ai_services.use'

function semRepetirEm4xx(falhas: number, erro: unknown): boolean {
  if (erro instanceof ApiError && erro.status >= 400 && erro.status < 500) return false
  return falhas < 2
}

function ErroDeAcao({ erro }: { erro: unknown }) {
  const n = activitiesLinkedCount(erro)
  if (n !== null) {
    return (
      <Alert severity="warning" data-testid="erro-acao">
        {t.descriptionEditor.hasActivities(n)}
      </Alert>
    )
  }
  return <ErrorState error={erro} />
}

// ------------------------------------------------------------------ cabecalho

function Cabecalho({
  d,
  jobId,
  onSalvar,
  onPendente,
  salvando,
  campoComErro,
}: {
  d: DescriptionVersion
  jobId: string
  onSalvar: (c: HeaderChanges) => void
  /** s60c / A2: o que esta escrito e AINDA NAO foi salvo (vazio = nada pendente) -- o Publicar usa. */
  onPendente: (c: HeaderChanges) => void
  salvando: boolean
  campoComErro: HeaderErrorField | null
}) {
  const unidades = useQuery({ queryKey: UNITS_QUERY_KEY, queryFn: fetchAllUnits, retry: semRepetirEm4xx })
  const cargos = useQuery({ queryKey: JOBS_QUERY_KEY, queryFn: fetchAllJobs, retry: semRepetirEm4xx })
  const [funcao, setFuncao] = useState(d.funcao ?? '')
  // s62 / C3b-2 (F3, ADR-0032): a FUNCAO e do CARGO. Com o catalogo lido, o campo so MOSTRA a funcao do cargo e, ao
  // salvar o cabecalho, a descricao guarda uma copia do nome (o documento publicado continua dizendo a funcao).
  const faixas = useQuery({ queryKey: BANDS_QUERY_KEY, queryFn: fetchBands, retry: false })
  const doCargo = faixas.isSuccess ? (functionByJob(faixas.data).get(jobId) ?? null) : null
  const soMostra = faixas.isSuccess
  const [area, setArea] = useState(d.areaUnitId ?? '')
  const [chefe, setChefe] = useState(d.reportsToJobId ?? '')

  // s60c / A3: so unidades ATIVAS (desativada nao recebe cargo novo), mais a atual do rascunho
  const opcoesArea = (unidades.data?.units ?? []).filter((u) => u.status === 'active' || u.id === d.areaUnitId)
  const opcoesChefe = (cargos.data ?? []).filter((j) => j.id !== jobId && (j.status !== 'archived' || j.id === d.reportsToJobId))

  const mudancas = useMemo(() => {
    const c: HeaderChanges = {}
    if (soMostra) {
      if (doCargo && doCargo.name !== (d.funcao ?? '')) c.funcao = doCargo.name
    } else if (funcao.trim() !== (d.funcao ?? '')) c.funcao = funcao.trim() === '' ? null : funcao.trim()
    if (area !== (d.areaUnitId ?? '')) c.areaUnitId = area === '' ? null : area
    if (chefe !== (d.reportsToJobId ?? '')) c.reportsToJobId = chefe === '' ? null : chefe
    return c
  }, [funcao, area, chefe, d.funcao, d.areaUnitId, d.reportsToJobId, soMostra, doCargo])
  useEffect(() => onPendente(mudancas), [mudancas, onPendente])

  function enviar(e: FormEvent) {
    e.preventDefault()
    if (Object.keys(mudancas).length > 0) onSalvar(mudancas)
  }

  return (
    <Paper component="form" variant="outlined" onSubmit={enviar} data-testid="cabecalho-form" sx={{ p: 2 }}>
      <Typography variant="h2" sx={{ fontSize: '1.125rem', mb: 1.5 }}>
        {t.descriptionEditor.headerTitle}
      </Typography>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))' }}>
        {soMostra ? (
          <TextField
            label={t.jobRecord.fieldFuncao}
            value={maiuscula(doCargo?.name ?? d.funcao ?? '')}
            size="small"
            data-testid="funcao-do-cargo"
            helperText={
              <RouterLink to={`/cargos/${encodeURIComponent(jobId)}`}>
                {doCargo ? t.descriptionEditor.functionFromJob : t.descriptionEditor.functionChooseInJob}
              </RouterLink>
            }
            slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
          />
        ) : (
          <TextField
            label={t.jobRecord.fieldFuncao}
            value={funcao}
            onChange={(e) => setFuncao(e.target.value)}
            size="small"
            slotProps={{ htmlInput: { maxLength: 128 } }}
          />
        )}
        <TextField
          select
          label={t.jobRecord.fieldArea}
          value={area}
          onChange={(e) => setArea(e.target.value)}
          size="small"
          error={campoComErro === 'area'}
          helperText={campoComErro === 'area' ? t.descriptionEditor.headerFieldError.area : undefined}
          slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
        >
          <option value="">{t.descriptionEditor.none}</option>
          {area !== '' && !opcoesArea.some((u) => u.id === area) && <option value={area}>{d.areaName ?? area}</option>}
          {opcoesArea.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </TextField>
        <TextField
          select
          label={t.jobRecord.fieldReportsTo}
          value={chefe}
          onChange={(e) => setChefe(e.target.value)}
          size="small"
          error={campoComErro === 'subordinacao'}
          helperText={campoComErro === 'subordinacao' ? t.descriptionEditor.headerFieldError.subordinacao : undefined}
          slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
        >
          <option value="">{t.descriptionEditor.none}</option>
          {chefe !== '' && !opcoesChefe.some((j) => j.id === chefe) && (
            <option value={chefe}>{d.reportsToName ?? chefe}</option>
          )}
          {opcoesChefe.map((j) => (
            <option key={j.id} value={j.id}>
              {j.name}
            </option>
          ))}
        </TextField>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Button type="submit" variant="outlined" size="small" disabled={salvando}>
          {t.descriptionEditor.saveHeader}
        </Button>
      </Box>
    </Paper>
  )
}

// ------------------------------------------------------------------ item

/**
 * s59 fatia 2 -- item redesenhado: alca de arrastar (nativo, T3), texto que se edita NO PROPRIO TEXTO (Enter salva,
 * Esc cancela), entrega da atividade como ETIQUETA, acoes que aparecem no hover/foco (existem sempre para teclado e
 * leitor de tela; so a opacidade muda) e menu "Mais acoes" com mover e excluir (o desfazer mora na pagina, T4).
 */
function LinhaItem({
  item,
  entregas,
  primeiro,
  ultimo,
  ocupado,
  onEditar,
  onMover,
  onExcluir,
  onArrastarInicio,
  onArrastarSobre,
  onSoltar,
}: {
  item: DraftItem
  entregas: DraftItem[]
  primeiro: boolean
  ultimo: boolean
  ocupado: boolean
  onEditar: (c: { text?: string; servesToItemId?: string | null; requirement?: ItemRequirement }) => void
  onMover: (novaPosicao: number) => void
  onExcluir: () => void
  onArrastarInicio: () => void
  onArrastarSobre: () => boolean
  onSoltar: () => void
}) {
  const [editando, setEditando] = useState(false)
  const [texto, setTexto] = useState(textoParaTela(item.text))
  const [menu, setMenu] = useState<HTMLElement | null>(null)
  const [escolhendoEntrega, setEscolhendoEntrega] = useState(false)
  const encerrado = useRef(false)
  const atividade = item.block === 'detalhamento'
  const comExigencia = REQUIREMENT_BLOCKS.includes(item.block)
  const entregaLigada = entregas.find((e) => e.id === item.servesToItemId)

  function abrirEdicao() {
    if (ocupado) return
    encerrado.current = false
    setTexto(textoParaTela(item.text))
    setEditando(true)
  }
  function salvar() {
    if (encerrado.current) return
    encerrado.current = true
    const v = texto.trim()
    setEditando(false)
    if (v !== '' && v !== textoParaTela(item.text)) onEditar({ text: v })
  }
  function cancelar() {
    encerrado.current = true
    setTexto(textoParaTela(item.text))
    setEditando(false)
  }
  function teclas(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      salvar()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelar()
    }
  }

  return (
    <Box
      component="li"
      data-testid={`item-${item.id}`}
      onDragOver={(e) => {
        if (onArrastarSobre()) e.preventDefault()
      }}
      onDrop={(e) => {
        e.preventDefault()
        onSoltar()
      }}
      sx={{
        listStyle: 'none',
        py: 0.75,
        borderTop: 1,
        borderColor: 'divider',
        minWidth: 0,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
        '& .acoes': { opacity: { xs: 1, md: 0 }, transition: 'opacity 120ms' },
        '&:hover .acoes, &:focus-within .acoes': { opacity: 1 },
        '@media (hover: none)': { '& .acoes': { opacity: 1 } },
      }}
    >
      <Box
        component="span"
        aria-label={t.descriptionEditor.dragHandle}
        title={t.descriptionEditor.dragHandle}
        draggable={!ocupado}
        onDragStart={(e) => {
          e.dataTransfer?.setData('text/plain', item.id)
          onArrastarInicio()
        }}
        className="acoes"
        sx={{ cursor: 'grab', color: 'text.secondary', display: 'flex', pt: 0.25 }}
      >
        <DragIndicator fontSize="small" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {editando ? (
          <TextField
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={teclas}
            onBlur={salvar}
            multiline
            autoFocus
            fullWidth
            size="small"
            label={t.descriptionEditor.edit}
            helperText={t.descriptionEditor.editHint}
          />
        ) : (
          <Typography
            variant="body2"
            title={t.descriptionEditor.clickToEdit}
            onClick={abrirEdicao}
            sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap', cursor: 'text', borderRadius: 0.5, '&:hover': { bgcolor: 'action.hover' } }}
          >
            {textoParaTela(item.text)}
          </Typography>
        )}

        {(atividade || comExigencia) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 0.5 }}>
            {comExigencia && (
              <TextField
                select
                label={t.descriptionEditor.requirementLabel}
                value={item.requirement ?? 'necessario'}
                onChange={(e) => onEditar({ requirement: e.target.value as ItemRequirement })}
                size="small"
                disabled={ocupado}
                slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                sx={{ minWidth: 140 }}
              >
                <option value="necessario">{t.descriptionEditor.necessario}</option>
                <option value="desejavel">{t.descriptionEditor.desejavel}</option>
              </TextField>
            )}
            {atividade &&
              (escolhendoEntrega ? (
                <TextField
                  select
                  autoFocus
                  label={t.descriptionEditor.linkLabel}
                  value={item.servesToItemId ?? ''}
                  onChange={(e) => {
                    setEscolhendoEntrega(false)
                    onEditar({ servesToItemId: e.target.value === '' ? null : e.target.value })
                  }}
                  onBlur={() => setEscolhendoEntrega(false)}
                  size="small"
                  disabled={ocupado}
                  slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                  sx={{ minWidth: 0, maxWidth: '100%', flex: '1 1 200px' }}
                >
                  <option value="">{t.descriptionEditor.transversal}</option>
                  {entregas.map((e) => (
                    <option key={e.id} value={e.id}>
                      {textoParaTela(e.text)}
                    </option>
                  ))}
                </TextField>
              ) : (
                <Chip
                  size="small"
                  variant="outlined"
                  disabled={ocupado}
                  label={entregaLigada ? t.descriptionEditor.servesTo(textoParaTela(entregaLigada.text)) : t.descriptionEditor.transversal}
                  onClick={() => setEscolhendoEntrega(true)}
                  // s59 "etiqueta discreta": cinza; a cor de destaque so no hover/foco -- quem manda na linha e a atividade
                  sx={{ maxWidth: '100%', color: 'text.secondary', '&:hover, &:focus-visible': { color: 'primary.main', borderColor: 'primary.main' } }}
                />
              ))}
          </Box>
        )}
      </Box>

      <Box className="acoes" sx={{ display: 'flex', flexShrink: 0 }}>
        <IconButton size="small" aria-label={t.descriptionEditor.edit} disabled={ocupado || editando} onClick={abrirEdicao}>
          <EditOutlined fontSize="small" />
        </IconButton>
        <IconButton size="small" aria-label={t.descriptionEditor.moreActions} disabled={ocupado} onClick={(e) => setMenu(e.currentTarget)}>
          <MoreVert fontSize="small" />
        </IconButton>
        <Menu anchorEl={menu} open={menu !== null} onClose={() => setMenu(null)}>
          <MenuItem disabled={primeiro} onClick={() => { setMenu(null); onMover(item.position - 1) }}>
            {t.descriptionEditor.moveUp}
          </MenuItem>
          <MenuItem disabled={ultimo} onClick={() => { setMenu(null); onMover(item.position + 1) }}>
            {t.descriptionEditor.moveDown}
          </MenuItem>
          <MenuItem onClick={() => { setMenu(null); onExcluir() }} sx={{ color: 'error.main' }}>
            {t.descriptionEditor.remove}
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  )
}

// ------------------------------------------------------------------ bloco

function NovoItem({
  bloco,
  ocupado,
  onAdicionar,
}: {
  bloco: DescriptionBlock
  ocupado: boolean
  onAdicionar: (texto: string, exigencia?: ItemRequirement) => void
}) {
  const [texto, setTexto] = useState('')
  const [exigencia, setExigencia] = useState<ItemRequirement>('necessario')
  const comExigencia = REQUIREMENT_BLOCKS.includes(bloco)
  const titulo = t.descriptionEditor.blockTitle[bloco]

  function enviar(e: FormEvent) {
    e.preventDefault()
    if (texto.trim() === '') return
    onAdicionar(texto.trim(), comExigencia ? exigencia : undefined)
    setTexto('')
  }

  return (
    <Box component="form" onSubmit={enviar} data-testid={`novo-${bloco}`} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
      <TextField
        label={t.descriptionEditor.newItemLabel(titulo)}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        size="small"
        multiline
        sx={{ flex: '1 1 240px', minWidth: 0 }}
      />
      {comExigencia && (
        <TextField
          select
          label={t.descriptionEditor.requirementLabel}
          value={exigencia}
          onChange={(e) => setExigencia(e.target.value as ItemRequirement)}
          size="small"
          slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
          sx={{ minWidth: 140 }}
        >
          <option value="necessario">{t.descriptionEditor.necessario}</option>
          <option value="desejavel">{t.descriptionEditor.desejavel}</option>
        </TextField>
      )}
      <Button type="submit" variant="outlined" size="small" disabled={ocupado || texto.trim() === ''}>
        {t.descriptionEditor.add}
      </Button>
    </Box>
  )
}

// ------------------------------------------------------------------ pagina

export function DescriptionEditorPage() {
  const { jobId = '' } = useParams()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [confirmando, setConfirmando] = useState(false)
  const [erroAcao, setErroAcao] = useState<unknown>(null)
  const [campoComErro, setCampoComErro] = useState<HeaderErrorField | null>(null)

  const me = useQuery({ queryKey: ME_QUERY_KEY, queryFn: fetchMe })
  const permissoes = me.data?.permissions ?? []
  const podeEscrever = permissoes.includes(PERM_WRITE)
  const podePublicar = permissoes.includes(PERM_PUBLISH)
  const [iaDesligada, setIaDesligada] = useState(isAiDisabledHere) // s59 D5
  const temPermIA = permissoes.includes(PERM_IA)
  // s61 / E4: sinal da API -- false esconde desde o inicio; true mostra, salvo se a rota ja recusou (403) nesta aba.
  // Enquanto o sinal nao chega, os botoes esperam (sem "piscar"). Se a leitura FALHAR (API antiga, rede), vale o
  // s59 D5: mostra e esconde depois do 1o aviso.
  const disponivel = useQuery({
    queryKey: aiAvailabilityKey(),
    queryFn: fetchAiAvailability,
    enabled: temPermIA,
    retry: false,
  })
  const podeIA = temPermIA && !iaDesligada && (disponivel.isSuccess ? disponivel.data : disponivel.isError)
  const [painelBloco, setPainelBloco] = useState<DescriptionBlock | null>(null)

  const job = useQuery({
    queryKey: jobRecordKey(jobId),
    queryFn: () => fetchJobRecord(jobId),
    enabled: podeEscrever,
    retry: semRepetirEm4xx,
  })
  const versoes = useQuery({
    queryKey: descriptionVersionsKey(jobId),
    queryFn: () => fetchDescriptionVersions(jobId),
    enabled: podeEscrever && job.isSuccess,
    retry: semRepetirEm4xx,
  })
  // O rascunho recem-aberto vale ja (id devolvido pelo POST), sem esperar a lista de versoes recarregar.
  const [abertoId, setAbertoId] = useState<string | null>(null)
  const rascunhoId = findDraft(versoes.data ?? [])?.id ?? abertoId
  const rascunho = useQuery({
    queryKey: descriptionKey(rascunhoId ?? ''),
    queryFn: () => fetchDescription(rascunhoId ?? ''),
    enabled: rascunhoId !== null,
    retry: semRepetirEm4xx,
  })
  const d = rascunho.data

  function aplicar(v: DescriptionVersion) {
    qc.setQueryData(descriptionKey(v.id), v)
    setErroAcao(null)
  }

  const abrir = useMutation({
    mutationFn: () => openDraft(jobId, draftSource(versoes.data ?? [])),
    onSuccess: (v) => {
      aplicar(v)
      setAbertoId(v.id)
      void qc.invalidateQueries({ queryKey: descriptionVersionsKey(jobId) })
    },
    onError: setErroAcao,
  })
  const cabecalho = useMutation({
    mutationFn: (c: HeaderChanges) => updateHeader(d?.id ?? '', c),
    onSuccess: (v) => {
      setCampoComErro(null)
      aplicar(v)
    },
    onError: (e) => {
      const campo = headerErrorField(e)
      setCampoComErro(campo)
      setErroAcao(campo === null ? e : null)
    },
  })
  const acao = useMutation({
    mutationFn: (f: () => Promise<DescriptionVersion | void>) => f(),
    onSuccess: (v) => {
      if (v) aplicar(v)
      else {
        setErroAcao(null)
        void qc.invalidateQueries({ queryKey: descriptionKey(d?.id ?? '') })
      }
    },
    onError: setErroAcao,
  })
  // s60c / A2: Publicar com o cabecalho escrito e NAO salvo publicava o cabecalho velho (defeito A2-s61). Agora o
  // dialogo avisa e o botao vira "Salvar e publicar": salva o cabecalho e SO DEPOIS publica.
  const [cabPendente, setCabPendente] = useState<HeaderChanges>({})
  const aoPendente = useCallback((c: HeaderChanges) => setCabPendente(c), [])
  const temPendente = Object.keys(cabPendente).length > 0
  const publicar = useMutation({
    mutationFn: async (c?: HeaderChanges) => {
      if (c && Object.keys(c).length > 0) aplicar(await updateHeader(d?.id ?? '', c))
      return publishDescription(d?.id ?? '')
    },
    onSuccess: async () => {
      setConfirmando(false)
      // refetchType 'all': a ficha esta FECHADA aqui (query inativa). So invalidar a deixaria abrir com o
      // "sem versao" do cache e mostrar o legado por um instante (achado s45, E12 intermitente). Buscando
      // de novo tambem as inativas, o await so resolve com a versao publicada ja no cache.
      await Promise.all([
        qc.invalidateQueries({ queryKey: currentDescriptionKey(jobId), refetchType: 'all' }),
        qc.invalidateQueries({ queryKey: descriptionVersionsKey(jobId), refetchType: 'all' }),
      ])
      qc.removeQueries({ queryKey: descriptionKey(d?.id ?? '') })
      navigate(`/cargos/${encodeURIComponent(jobId)}`)
    },
    onError: (e) => {
      setConfirmando(false)
      const campo = headerErrorField(e)
      setCampoComErro(campo)
      setErroAcao(campo === null ? e : null)
    },
  })

  // s59 fatia 2 (T4): excluir apaga NA HORA e oferece Desfazer por 8 s (antes apagava sem confirmar e sem volta, A1-s57).
  const [desfazer, setDesfazer] = useState<{ item: DraftItem; ultimo: boolean } | null>(null)
  // s69 / 2s (Marcelo 29/10 10:30, "comando duplo"): excluir pede confirmacao ANTES; o Desfazer (T4, s59) continua
  const [confirmar, setConfirmar] = useState<Confirmacao | null>(null)
  const excluir = useMutation({
    mutationFn: (x: { item: DraftItem; ultimo: boolean }) => removeItem(d?.id ?? '', x.item.id),
    onSuccess: (_v, x) => {
      setErroAcao(null)
      setDesfazer(x)
      void qc.invalidateQueries({ queryKey: descriptionKey(d?.id ?? '') })
    },
    onError: setErroAcao,
  })
  const restaurar = useMutation({
    mutationFn: async (x: { item: DraftItem; ultimo: boolean }) => {
      const idsAntes = new Set((d?.items ?? []).map((i) => i.id))
      const novo: { block: DescriptionBlock; text: string; requirement?: ItemRequirement; servesToItemId?: string } = {
        block: x.item.block,
        text: x.item.text,
      }
      if (x.item.requirement !== null) novo.requirement = x.item.requirement
      if (x.item.servesToItemId !== null) novo.servesToItemId = x.item.servesToItemId
      const v = await addItem(d?.id ?? '', novo)
      if (x.ultimo) return v
      // recriado vai para o FIM do bloco; volta a posicao antiga
      const criado = v.items.find((i) => i.block === x.item.block && !idsAntes.has(i.id))
      return criado ? moveItem(v.id, criado.id, x.item.position) : v
    },
    onSuccess: (v) => {
      setDesfazer(null)
      aplicar(v)
    },
    onError: setErroAcao,
  })
  // s59 fatia 2 (T3): arrasto NATIVO, so dentro do mesmo bloco (cada item pertence a um bloco do metodo).
  const [arrastando, setArrastando] = useState<DraftItem | null>(null)

  const ocupado = abrir.isPending || cabecalho.isPending || acao.isPending || publicar.isPending || excluir.isPending || restaurar.isPending

  const voltar = (
    <Box>
      <Button size="small" component={RouterLink} to={`/cargos/${encodeURIComponent(jobId)}`} startIcon={<ArrowBack fontSize="small" />}>
        {t.descriptionEditor.back}
      </Button>
    </Box>
  )

  if (me.isPending) return <LoadingState />
  if (!podeEscrever) {
    return (
      <Stack spacing={2}>
        {voltar}
        <Alert severity="info" data-testid="sem-permissao">
          {t.descriptionEditor.noPermission}
        </Alert>
      </Stack>
    )
  }

  const publicada = (versoes.data ?? []).filter((v) => v.status === 'publicada').sort((a, z) => z.versionNumber - a.versionNumber)[0]

  return (
    <Stack spacing={2} sx={{ minWidth: 0, overflowWrap: 'anywhere', pr: { sm: painelBloco !== null ? '416px' : 0 } }}>
      {voltar}
      {(job.isPending || versoes.isPending) && <LoadingState />}
      {job.isError && <ErrorState error={job.error} onRetry={() => void job.refetch()} />}
      {versoes.isError && <ErrorState error={versoes.error} onRetry={() => void versoes.refetch()} />}

      {job.isSuccess && (
        <Box
          data-testid="cabecalho-fixo"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: 'background.default',
            py: 1,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h1" sx={{ fontSize: '1.5rem', overflowWrap: 'anywhere', flex: '1 1 auto' }}>
            {job.data.name}
          </Typography>
          {d && <Chip label={t.descriptionEditor.draftLabel(d.versionNumber)} color="info" variant="outlined" size="small" />}
          {d && podePublicar && (
            <Button variant="contained" disabled={ocupado} onClick={() => setConfirmando(true)}>
              {t.descriptionEditor.publish}
            </Button>
          )}
        </Box>
      )}

      {erroAcao !== null && <ErroDeAcao erro={erroAcao} />}

      {versoes.isSuccess && rascunhoId === null && (
        <Paper variant="outlined" data-testid="sem-rascunho" sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t.descriptionEditor.noDraftHint}
          </Typography>
          <Button variant="contained" disabled={ocupado} onClick={() => abrir.mutate()}>
            {publicada ? t.descriptionEditor.startFromPublished(publicada.versionNumber) : t.descriptionEditor.startEmpty}
          </Button>
        </Paper>
      )}

      {rascunhoId !== null && rascunho.isPending && <LoadingState />}
      {rascunho.isError && <ErrorState error={rascunho.error} onRetry={() => void rascunho.refetch()} />}

      {d && (
        <>
          <Cabecalho
            key={`${d.id}-${d.areaUnitId}-${d.reportsToJobId}-${d.funcao}`}
            d={d}
            jobId={jobId}
            salvando={ocupado}
            campoComErro={campoComErro}
            onSalvar={(c) => cabecalho.mutate(c)}
            onPendente={aoPendente}
          />

          {BLOCK_ORDER.map((bloco) => {
            const itens = itemsOfBlock(d.items, bloco)
            const entregas = linkableDeliverables(d.items)
            return (
              <Paper key={bloco} component="section" variant="outlined" data-testid={`bloco-${bloco}`} aria-labelledby={`bl-${bloco}`} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography id={`bl-${bloco}`} variant="h2" sx={{ fontSize: '1.125rem', flex: 1 }}>
                    {t.descriptionEditor.blockTitle[bloco]}
                  </Typography>
                  {podeIA && SUGGESTABLE_BLOCKS.includes(bloco) && (
                    <Button size="small" startIcon={<VigaIcon src={VIGA.icone} size={20} testId="viga-botao" />} onClick={() => setPainelBloco(bloco)}>
                      {t.aiPanel.suggest}
                    </Button>
                  )}
                </Box>
                {bloco === 'detalhamento' && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t.descriptionEditor.activitiesHint}
                  </Typography>
                )}
                {/* s69 / 2o (V2): ha atividades sem entrega -> convite para a VIGA sugerir entregas (mesmo painel do bloco) */}
                {bloco === 'entregas' && podeIA && transversalActivities(d.items).length > 0 && (
                  <Alert
                    severity="info"
                    data-testid="convite-transversais"
                    sx={{ mb: 1 }}
                    action={
                      <Button size="small" onClick={() => setPainelBloco('entregas')}>
                        {t.descriptionEditor.transversalSuggest}
                      </Button>
                    }
                  >
                    {t.descriptionEditor.transversalInvite(transversalActivities(d.items).length)}
                  </Alert>
                )}
                {itens.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t.descriptionEditor.empty}
                  </Typography>
                ) : (
                  <Box component="ul" sx={{ m: 0, p: 0 }}>
                    {itens.map((i, k) => (
                      <LinhaItem
                        key={`${i.id}-${i.text}`}
                        item={i}
                        entregas={entregas}
                        primeiro={k === 0}
                        ultimo={k === itens.length - 1}
                        ocupado={ocupado}
                        onEditar={(c) => acao.mutate(() => editItem(d.id, i.id, c))}
                        onMover={(p) => acao.mutate(() => moveItem(d.id, i.id, p))}
                        onExcluir={() => setConfirmar({ mensagem: t.descriptionEditor.confirmDeleteItem(i.text), acao: () => excluir.mutate({ item: i, ultimo: k === itens.length - 1 }) })}
                        onArrastarInicio={() => setArrastando(i)}
                        onArrastarSobre={() => arrastando !== null && arrastando.block === i.block && arrastando.id !== i.id}
                        onSoltar={() => {
                          const origem = arrastando
                          setArrastando(null)
                          if (origem && origem.block === i.block && origem.id !== i.id) {
                            acao.mutate(() => moveItem(d.id, origem.id, i.position))
                          }
                        }}
                      />
                    ))}
                  </Box>
                )}
                {canAddItem(bloco, d.items) ? (
                  <NovoItem
                    bloco={bloco}
                    ocupado={ocupado}
                    onAdicionar={(texto, exigencia) =>
                      acao.mutate(() => addItem(d.id, { block: bloco, text: texto, ...(exigencia ? { requirement: exigencia } : {}) }))
                    }
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                    {t.descriptionEditor.singleItemHint}
                  </Typography>
                )}
              </Paper>
            )
          })}

          {painelBloco !== null && (
            <AiSuggestionPanel
              descriptionId={d.id}
              block={painelBloco}
              items={d.items}
              onApplied={aplicar}
              onClose={() => setPainelBloco(null)}
              onNotEnabled={() => {
                markAiDisabledHere()
                setIaDesligada(true)
              }}
            />
          )}

          <ConfirmDialog pedido={confirmar} fechar={() => setConfirmar(null)} />

          <Snackbar
            open={desfazer !== null}
            autoHideDuration={8000}
            onClose={(_e, motivo) => {
              if (motivo !== 'clickaway') setDesfazer(null)
            }}
            message={t.descriptionEditor.deleted}
            action={
              <Button color="inherit" size="small" disabled={restaurar.isPending} onClick={() => desfazer && restaurar.mutate(desfazer)}>
                {t.descriptionEditor.undo}
              </Button>
            }
          />

          <Dialog open={confirmando} onClose={() => setConfirmando(false)} aria-labelledby="publicar-titulo">
            <DialogTitle id="publicar-titulo">{t.descriptionEditor.publishTitle(d.versionNumber)}</DialogTitle>
            <DialogContent>
              <DialogContentText>{t.descriptionEditor.publishBody}</DialogContentText>
              {temPendente && (
                <Alert severity="warning" data-testid="cabecalho-pendente" sx={{ mt: 2 }}>
                  {t.descriptionEditor.headerUnsaved}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmando(false)}>{t.descriptionEditor.cancel}</Button>
              <Button variant="contained" disabled={publicar.isPending} onClick={() => publicar.mutate(temPendente ? cabPendente : undefined)}>
                {temPendente ? t.descriptionEditor.saveAndPublish : t.descriptionEditor.publishConfirm}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Stack>
  )
}
